import { createContext, useEffect, useRef, type ReactNode } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useReducedMotion } from '@/hooks/useReducedMotion';

gsap.registerPlugin(ScrollTrigger);

export const LenisContext = createContext<Lenis | null>(null);

/**
 * Wraps the app. Instantiates ONE Lenis instance and syncs ScrollTrigger.
 * Bypasses Lenis when reduced motion is active.
 */
export function ScrollProvider({ children }: { children: ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) {
      // No Lenis under reduced motion. ScrollTrigger still works with native scroll.
      return;
    }
    const lenis = new Lenis({
      // v1.0 — tightened from 1.15s to keep scroll-driven video scrub responsive
      duration: 0.4,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 1.2,
    });
    lenisRef.current = lenis;

    const onScroll = () => ScrollTrigger.update();
    lenis.on('scroll', onScroll);

    const raf = (time: number) => {
      lenis.raf(time * 1000);
    };
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    // v1.0.2 — fix wheel-scroll freeze around the hero pin boundary. Without
    // normalizeScroll, native wheel events can be consumed-without-effect when
    // crossing into or out of a ScrollTrigger pin while Lenis is mid-tween
    // (symptom: page only advances when the user grabs the scrollbar manually).
    // Recommended setup for any Lenis + scrub:true + pin:true combination.
    ScrollTrigger.normalizeScroll(true);

    return () => {
      lenis.off('scroll', onScroll);
      gsap.ticker.remove(raf);
      ScrollTrigger.normalizeScroll(false);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [reducedMotion]);

  return (
    <LenisContext.Provider value={lenisRef.current}>
      {children}
    </LenisContext.Provider>
  );
}
